/**
 * Format a number as dollar currency with two decimals (e.g. 8000.5 -> "$8,000.50").
 */
export const formatCurrency = (value: number): string => {
  return `$${value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

/**
 * Format a number as en-US currency display (e.g. 8000 -> "8,000.00").
 * Returns empty string for NaN or negative.
 */
export const formatCurrencyDisplay = (value: number): string => {
  if (Number.isNaN(value) || value < 0) return '';
  return Math.round(value).toLocaleString('en-US');
};

/**
 * Parse a whole-dollar currency-style input string to a number (strips commas).
 * Returns NaN for empty or invalid values.
 */
export const parseCurrencyInput = (raw: string): number => {
  const cleaned = raw.replace(/,/g, '').trim();
  if (!/^\d+$/.test(cleaned)) return Number.NaN;
  return Number(cleaned);
};

/**
 * Sanitize user input to allow only whole-dollar values (digits/commas).
 * Any decimal portion is discarded.
 * Use for controlled currency/numeric input fields.
 */
export const sanitizeNumericInput = (value: string): string => {
  const [beforeDecimal] = value.split('.');
  return beforeDecimal.replace(/[^0-9,]/g, '');
};

/**
 * Format a dollar amount in compact form for tight UI (e.g. 0 -> "0", 5000 -> "5K", 5500 -> "5.5K").
 */
export const formatCurrencyCompact = (amount: number): string => {
  const n = Math.round(amount);
  if (n >= 1000) {
    const k = n / 1000;
    const rounded = Number(k.toFixed(1));
    return `${rounded}K`;
  }
  return String(n);
};
